import { ValidationErrorDetail, ValidationStatusEntity } from 'src/models/XptGenericModels';
import { ValidationService } from './ValidationService';
import { ForecastTemplateColumns } from 'src/models/ForecastModels';
import ForecastValidationMessages from '../forecast-utils/ValidationMessages';
import { ForecastGridFixedFields } from '../forecast-utils/ForecastGridConstants';
import { logger } from 'src/analytics/KatalLogger';

/**
 * Service for validating duplicate records in forecast templates
 */
export class DuplicateRecordValidationService extends ValidationService {
  /**
   * Checks for duplicate records based on the combination of all the Corp Segment Fields & Business Segment ID Fields within the relevant row data.
   * (Doesn't consider Budget Owner while checking for duplicate records)
   * @param forecastTemplateRelevantRowData An array of relevant rows from the forecast template to be validated.
   * @param forecastTemplateColumns An object containing column definitions, specifically corpSegmentMandatoryFields and businessSegmentMandatoryFields.
   * @returns Promise with validation status
   */
  public async validateDuplicateRecords(
    forecastTemplateRelevantRowData: any[],
    forecastTemplateColumns: ForecastTemplateColumns
  ): Promise<ValidationStatusEntity> {
    const validationErrorDetails: ValidationErrorDetail[] = [];
    logger.info(`Validating duplicate records for ${forecastTemplateRelevantRowData.length} records`);

    const corpSegmentFieldsWithoutDescription = forecastTemplateColumns.corpSegmentMandatoryFields.filter((field) => !field.endsWith(' Description'));

    // Combine all the Corporate Segment Fields and Business Segment ID Fields into a single array
    const idColumns = corpSegmentFieldsWithoutDescription.concat(forecastTemplateColumns.businessSegmentMandatoryFields);

    // Set to keep track of unique combinations
    const uniqueCombinations = new Set<string>();

    // Iterate over each row in the relevant row data
    forecastTemplateRelevantRowData.forEach((row, rowIndex) => {
      const rowNo = (row[ForecastGridFixedFields.FileRowNumber.value] as number) ?? rowIndex + 1;
      // Create a unique key for the combination of all ID columns
      const combinationKey = idColumns.map((column) => row[column]).join('|');

      // Check if the combination key already exists in the set
      if (uniqueCombinations.has(combinationKey)) {
        validationErrorDetails.push({
          rowIndex: rowNo,
          message: `Duplicate record found ${this.getLineItemIdMessagePart(row, ForecastGridFixedFields.XptLineItemId.value)}`
        });
      } else {
        uniqueCombinations.add(combinationKey);
      }
    });

    // Log validation errors if any
    this.logValidationErrors('Duplicate Records', validationErrorDetails);

    // Return appropriate validation status
    if (validationErrorDetails.length > 0) {
      return this.createErrorStatus(
        ForecastValidationMessages.DUPLICATE_RECORDS_FOUND,
        ForecastValidationMessages.DUPLICATE_RECORDS_VALIDATION_DEFAULT,
        validationErrorDetails
      );
    }

    return this.createSuccessStatus(ForecastValidationMessages.NO_DUPLICATE_RECORDS, ForecastValidationMessages.DUPLICATE_RECORDS_VALIDATION_DEFAULT);
  }

  /**
   * Checks for duplicate records based on the combination of All the Corp Segment Fields & Business Segment ID Fields against existing data.
   * @param forecastTemplateRelevantRowData An array of relevant rows from the forecast template to be validated.
   * @param forecastTemplateColumns An object containing column definitions.
   * @param forecastTemplateCompleteData An array of all rows from the forecast template for checking duplicates against.
   * @returns Promise with validation status
   */
  public async validateDuplicateRecordsWhileUpload(
    forecastTemplateRelevantRowData: any[],
    forecastTemplateColumns: ForecastTemplateColumns,
    forecastTemplateCompleteData: any[]
  ): Promise<ValidationStatusEntity> {
    const validationErrorDetails: ValidationErrorDetail[] = [];

    logger.info(
      `validateDuplicateRecordsWhileUpload: Validating Records ${forecastTemplateRelevantRowData.length} against complete set ${forecastTemplateCompleteData.length}`
    );

    // Check if complete data is empty or undefined
    if (!forecastTemplateCompleteData || forecastTemplateCompleteData.length === 0) {
      return this.createSuccessStatus(ForecastValidationMessages.NO_EXISTING_RECORDS, '');
    }

    const corpSegmentFieldsWithoutDescription = forecastTemplateColumns.corpSegmentMandatoryFields.filter((field) => !field.endsWith(' Description'));

    // Combine all the Corporate Segment Fields and Business Segment ID Fields into a single array
    const idColumns = corpSegmentFieldsWithoutDescription.concat(forecastTemplateColumns.businessSegmentMandatoryFields);

    logger.info(`validateDuplicateRecordsWhileUpload: Validating for columns ${idColumns}`);

    // Iterate over each row in the relevant row data
    forecastTemplateRelevantRowData.forEach((row, rowIndex) => {
      const rowNo = (row[ForecastGridFixedFields.FileRowNumber.value] as number) ?? rowIndex + 1;
      try {
        // Check if there is any existing row in the complete data that matches the current row
        const hasMatch = forecastTemplateCompleteData.some((existingRow) => {
          // Check if all the ID columns match between the current row and the existing row
          return idColumns.every((column) => row[column] === existingRow[column]);
        });

        if (hasMatch) {
          validationErrorDetails.push({
            rowIndex: rowNo,
            message: `Some of the records already exists ${this.getLineItemIdMessagePart(row, ForecastGridFixedFields.XptLineItemId.value)}`
          });
        }
      } catch (error) {
        logger.error('Error while checking for duplicate records:', { error: error });
        validationErrorDetails.push({
          rowIndex: rowNo,
          message: `Error validating row ${this.getLineItemIdMessagePart(row, ForecastGridFixedFields.XptLineItemId.value)}`
        });
      }
    });

    // Log validation errors if any
    this.logValidationErrors('Existing Records', validationErrorDetails);

    // Return appropriate validation status
    if (validationErrorDetails.length > 0) {
      return this.createErrorStatus(
        ForecastValidationMessages.EXISTING_RECORDS_FOUND,
        ForecastValidationMessages.EXISTING_RECORDS_VALIDATION_DEFAULT,
        validationErrorDetails
      );
    }

    return this.createSuccessStatus(ForecastValidationMessages.NO_EXISTING_RECORDS, '');
  }
}

// Export singleton instance
export const duplicateRecordValidationService = new DuplicateRecordValidationService();
